import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/feature/PageTransition';

const LocationPage = () => {
  useEffect(() => {
    document.title = '삼원테크 | 오시는 길 - 전남 화순군 동면 동농공길 26-3';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 오시는 길. 주소: 전남 화순군 동면 동농공길 26-3. 전화: 061-373-3221. 버스·자가용 교통편 안내. 평일 08:00~18:00 운영.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "삼원테크",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "telephone": "+82-61-373-3221",
      "email": "samwontk@hanmail.net",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "동농공길 26-3",
        "addressLocality": "화순군 동면",
        "addressRegion": "전라남도",
        "postalCode": "58126",
        "addressCountry": "KR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.0658,
        "longitude": 126.9869
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "08:00",
          "closes": "18:00"
        }
      ],
      "hasMap": "https://maps.google.com/?q=전남+화순군+동면+동농공길+26-3",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "오시는 길", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/location` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-location';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-location')?.remove(); };
  }, []);

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: '주소',
      content: '전남 화순군 동면 동농공길 26-3',
      detail: '(전라남도 화순군)',
    },
    {
      icon: 'ri-phone-line',
      title: '전화',
      content: '061-373-3221',
      detail: '평일 08:00 - 18:00',
    },
    {
      icon: 'ri-mail-line',
      title: '이메일',
      content: 'samwontk@hanmail.net',
      detail: '24시간 접수 가능',
    },
    {
      icon: 'ri-time-line',
      title: '영업시간',
      content: '평일 08:00 - 18:00(점심:12:00-13:00)',
      detail: '토/일/공휴일 휴무',
    },
  ];

  const transportInfo = [
    {
      type: '버스',
      icon: 'ri-bus-line',
      routes: [
        '화순 시내버스 218번 → 동면 농공단지 하차',
        '화순 시내버스 220번 → 동면사무소 하차 후 도보 10분',
        '광주 시외버스 → 화순터미널 하차 후 218번 환승',
        '동면 농공단지 방면 버스 종점 인근 위치',
      ],
    },
    {
      type: '자가용',
      icon: 'ri-car-line',
      routes: [
        '광주 방면: 광주-화순 간 국도 29호선 이용 → 동면 방향',
        '화순읍에서 동면 방향 지방도 822호선 진입',
        '동면 농공단지 입구 표지판 따라 진입 → 동농공길 26-3',
        '내비게이션: "삼원테크" 또는 "동농공길 26-3" 검색',
      ],
    },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="relative pt-0 pb-0 h-[520px] overflow-hidden">
          {/* 배경 이미지 */}
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/8a8ef26a634019068f1534bbd0b973e2.jpeg"
              alt="삼원테크 전경"
              className="w-full h-full object-cover object-center"
            />
            {/* 자연스러운 어두운 오버레이 */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/55"></div>
          </div>

          {/* 텍스트 콘텐츠 */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center max-w-3xl mx-auto"
            >
              <p className="text-teal-300 text-sm font-semibold tracking-widest uppercase mb-4">SAMWONTECH</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-lg">
                오시는 길
              </h1>
              <p className="text-lg md:text-xl text-white/85 leading-relaxed drop-shadow">
                삼원테크를 방문해주셔서 감사합니다.<br />
                아래 지도와 교통편 안내를 참고하시기 바랍니다.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-2xl mb-6">
                    <i className={`${info.icon} text-3xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{info.title}</h3>
                  <p className="text-base font-semibold text-gray-800 mb-2">{info.content}</p>
                  <p className="text-sm text-gray-600">{info.detail}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Transportation Info */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                교통편 안내
              </h2>
              <p className="text-lg text-gray-600">
                다양한 교통수단으로 편리하게 방문하실 수 있습니다.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {transportInfo.map((transport, index) => (
                <motion.div
                  key={transport.type}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border-2 border-teal-100 hover:border-teal-300 transition-all duration-300"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-xl mr-4">
                      <i className={`${transport.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{transport.type}</h3>
                  </div>
                  <ul className="space-y-3">
                    {transport.routes.map((route, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 text-xl mr-2 mt-0.5"></i>
                        <span className="text-base text-gray-700 leading-relaxed">{route}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Info */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-information-line text-teal-600 text-3xl mr-3"></i>
                    방문 안내
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                      <span className="text-base text-gray-700">방문 전 사전 예약을 권장합니다.</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5"></i>
                      <span className="text-base text-gray-700">주차공간은 회사 안에 있습니다.</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-customer-service-2-line text-teal-600 text-3xl mr-3"></i>
                    문의하기
                  </h3>
                  <p className="text-base text-gray-700 mb-6 leading-relaxed">
                    방문 예약 및 기타 문의사항은 전화 또는 이메일로 연락주시기 바랍니다. 
                    전문 상담원이 친절하게 안내해드리겠습니다.
                  </p>
                  <div className="space-y-4">
                    <a
                      href="tel:061-373-3221"
                      className="flex items-center justify-center px-6 py-4 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-phone-line text-xl mr-2"></i>
                      전화 상담하기
                    </a>
                    <a
                      href="mailto:info@samwontech.com"
                      className="flex items-center justify-center px-6 py-4 bg-gray-100 text-gray-900 font-bold rounded-xl hover:bg-gray-200 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-line text-xl mr-2"></i>
                      이메일 문의하기
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default LocationPage;